import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Mail, Check } from "lucide-react";
import api from "@/lib/api";

// Forgot Password screen — captures the user's email and posts to
// /api/auth/password-reset-request. Backend records the request (and will
// dispatch an email once Resend/SendGrid is wired). Friendly success state.

export default function ForgotPasswordScreen() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/password-reset-request", { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      // Even on error, we show success for security (don't leak whether email exists).
      // We only surface a real error for network failure.
      const status = err?.response?.status;
      if (!status) {
        setError("Network hiccup. Check your connection and try again.");
      } else {
        setSubmitted(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="screen fade-in" data-testid="forgot-password-screen">
      <div className="top-bar">
        <button
          type="button"
          className="icon-btn"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="forgot-back-btn"
        >
          <ChevronLeft size={18} strokeWidth={1.7} />
        </button>
        <div className="logo-mark">RESET</div>
        <div style={{ width: 36 }} />
      </div>

      {!submitted ? (
        <>
          <div className="eyebrow" style={{ marginBottom: 8 }}>FORGOT PASSWORD</div>
          <h2 className="h1" style={{ fontSize: 30, marginBottom: 14, lineHeight: 1.12 }}>
            Reset your<br />
            <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>password.</em>
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", marginTop: 0, marginBottom: 22 }}>
            Enter the email address associated with your account, and we&apos;ll send you a secure link to reset your password.
          </p>

          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div className="field">
              <label className="label" htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                required
                autoFocus
                autoComplete="email"
                className="input"
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-email-input"
              />
            </div>

            {error && (
              <div className="error-text" data-testid="forgot-error" style={{ fontSize: 13 }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
              data-testid="forgot-submit-btn"
              style={{ marginTop: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}
            >
              {submitting ? (
                <>
                  <span className="spinner" />
                  <span>Sending instructions…</span>
                </>
              ) : (
                <>
                  <Mail size={15} strokeWidth={1.8} />
                  <span>Send reset link</span>
                </>
              )}
            </button>
          </form>
        </>
      ) : (
        <div style={{ textAlign: "center", paddingTop: 60 }} data-testid="forgot-success">
          <div
            style={{
              width: 64, height: 64, borderRadius: "50%",
              background: "rgba(232,211,185,0.15)", color: "var(--gold)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px",
            }}
          >
            <Check size={28} strokeWidth={1.8} />
          </div>
          <h2 className="h1" style={{ fontSize: 28, marginBottom: 14, lineHeight: 1.15 }}>
            Check your email.
          </h2>
          <p className="body" style={{ color: "var(--text-dim)", maxWidth: 320, margin: "0 auto" }}>
            If an account exists for <strong style={{ color: "var(--text)" }}>{email}</strong>, you&apos;ll get reset instructions in the next few minutes.
          </p>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate("/", { replace: true })}
            data-testid="forgot-back-to-login"
            style={{ marginTop: 28, width: "100%", justifyContent: "center" }}
          >
            Back to sign in
          </button>
        </div>
      )}
    </div>
  );
}
